import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

// --- 1. Shared Styles ---
export const outpostStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600&display=swap');

  .outpost-page {
    background-color: #fff;
  }
  .font-heading { 
    font-family: 'Instrument Serif', serif; 
  }
  .font-body { 
    font-family: 'Barlow', sans-serif; 
  }

  .liquid-glass {
    background: rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 0, 0, 0.03);
    position: relative;
    overflow: hidden;
  }

  .liquid-glass-strong {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(32px);
    -webkit-backdrop-filter: blur(32px);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.9), 0 8px 24px rgba(0, 0, 0, 0.06);
    position: relative;
    overflow: hidden;
  }
`;

// --- 2. Shared Navbar Component ---
export const OutpostNavbar = () => {
  const location = useLocation();
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Branding', path: '/brand-development' },
    { name: 'Content', path: '/content-production' },
    { name: 'E-Comm', path: '/ecommerce-optimization' },
    { name: 'Marketing', path: '/digital-marketing' },
    { name: 'Social', path: '/social-media-marketing' },
    { name: 'Portfolio', path: '/portfolio' }
  ];

  return (
    <nav className="fixed top-4 w-full px-8 lg:px-16 z-50 flex justify-between items-center">
      <Link to="/" className="w-12 h-12 rounded-full liquid-glass flex items-center justify-center p-2 hover:bg-black/5 transition-colors">
        <img 
          src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/cd6c64635923cfb3391950ed67895587.png" 
          alt="Logo" 
          className="w-full h-full object-contain" 
        />
      </Link>

      <div className="hidden lg:flex liquid-glass rounded-full px-1.5 py-1.5 items-center">
        {navLinks.map((link) => (
          <Link 
            key={link.name} 
            to={link.path} 
            className={`px-3 py-2 text-sm font-medium font-body transition-colors ${
              location.pathname === link.path ? 'text-black font-bold' : 'text-black/70 hover:text-black'
            }`}
          >
            {link.name}
          </Link>
        ))}
        <Link to="/contact" className="ml-1 bg-black text-white rounded-full px-4 py-2 text-sm font-medium font-body flex items-center gap-1 whitespace-nowrap hover:bg-black/80 transition-colors">
          Start a Project
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M7 17L17 7"/><path d="M7 7h10v10"/></svg>
        </Link>
      </div>

      <div className="w-12 h-12"></div>
    </nav>
  );
};

// --- 3. Shared FadingVideo Component ---
export const FadingVideo = ({ src, className, style, maxOpacity = 1 }) => {
  const videoRef = useRef(null);
  const fadingOutRef = useRef(false);
  const rafRef = useRef(null);

  const fadeTo = (target, duration) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const startOp = parseFloat(videoRef.current?.style.opacity || '0');
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentOp = startOp + (target - startOp) * progress;
      
      if (videoRef.current) {
        videoRef.current.style.opacity = currentOp.toString();
      }
      
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      video.style.opacity = '0';
      video.play().catch(() => {});
      fadeTo(maxOpacity, 500);
    };

    const handleTimeUpdate = () => {
      const remaining = video.duration - video.currentTime;
      if (!fadingOutRef.current && remaining <= 0.55 && remaining > 0) {
        fadingOutRef.current = true;
        fadeTo(0, 500);
      }
    };

    const handleEnded = () => {
      video.style.opacity = '0';
      setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(() => {});
        fadingOutRef.current = false;
        fadeTo(maxOpacity, 500);
      }, 100);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [maxOpacity]);

  return (
    <video
      ref={videoRef}
      src={src}
      className={className}
      style={{ ...style, opacity: 0 }}
      autoPlay
      muted
      playsInline
      preload="auto"
    />
  );
};

// --- 4. Shared BlurText Component ---
export const BlurText = ({ text, className, justify = 'center' }) => {
  const words = text.split(" ");
  return (
    <p className={className} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: justify, rowGap: '0.1em' }}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ filter: 'blur(10px)', opacity: 0, y: 50 }}
          whileInView={{ filter: ['blur(10px)', 'blur(5px)', 'blur(0px)'], opacity: [0, 0.5, 1], y: [50, -5, 0] }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.7, times: [0, 0.5, 1], ease: 'easeOut', delay: (i * 100) / 1000 }}
          style={{ display: 'inline-block', marginRight: '0.28em' }}
        >
          {word}
        </motion.span>
      ))}
    </p>
  );
};